/**
 * ActivityLog.tsx
 * Activity log viewer for audit trail and compliance monitoring.
 * Shows user activities with filtering and pagination.
 */

import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  InputAdornment,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Login as LoginIcon,
  Logout as LogoutIcon,
  PersonAdd as PersonAddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Assignment as AssignmentIcon,
  Business as BusinessIcon,
  Security as SecurityIcon,
  Download as DownloadIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

interface ActivityLogEntry {
  id: number;
  action: string;
  action_display: string;
  resource_type: string;
  resource_name: string;
  ip_address: string | null;
  created_at: string;
}

interface ActivityLogResponse {
  total: number;
  limit: number;
  offset: number;
  activities: ActivityLogEntry[];
}

// Action type to icon and color mapping
const actionConfig: Record<string, { icon: React.ReactNode; color: string; bgColor: string }> = {
  login: { icon: <LoginIcon fontSize="small" />, color: '#43A047', bgColor: '#E8F5E9' },
  logout: { icon: <LogoutIcon fontSize="small" />, color: '#FFA000', bgColor: '#FFF8E1' },
  login_failed: { icon: <SecurityIcon fontSize="small" />, color: '#D32F2F', bgColor: '#FFEBEE' },
  password_reset: { icon: <SecurityIcon fontSize="small" />, color: '#1976D2', bgColor: '#E3F2FD' },
  password_change: { icon: <SecurityIcon fontSize="small" />, color: '#1976D2', bgColor: '#E3F2FD' },
  email_change: { icon: <EditIcon fontSize="small" />, color: '#7B1FA2', bgColor: '#F3E5F5' },
  profile_update: { icon: <EditIcon fontSize="small" />, color: '#00796B', bgColor: '#E0F2F1' },
  account_created: { icon: <PersonAddIcon fontSize="small" />, color: '#43A047', bgColor: '#E8F5E9' },
  account_deleted: { icon: <DeleteIcon fontSize="small" />, color: '#D32F2F', bgColor: '#FFEBEE' },
  data_export: { icon: <DownloadIcon fontSize="small" />, color: '#0288D1', bgColor: '#E1F5FE' },
  audit_created: { icon: <AssignmentIcon fontSize="small" />, color: '#E53935', bgColor: '#FFEBEE' },
  audit_completed: { icon: <AssignmentIcon fontSize="small" />, color: '#43A047', bgColor: '#E8F5E9' },
  template_created: { icon: <AssignmentIcon fontSize="small" />, color: '#7B1FA2', bgColor: '#F3E5F5' },
  company_created: { icon: <BusinessIcon fontSize="small" />, color: '#1976D2', bgColor: '#E3F2FD' },
  team_invite: { icon: <PersonAddIcon fontSize="small" />, color: '#00796B', bgColor: '#E0F2F1' },
  permission_change: { icon: <SecurityIcon fontSize="small" />, color: '#FFA000', bgColor: '#FFF8E1' },
};

const ActivityLog: React.FC = () => {
  const { user } = useAuth();
  const [activities, setActivities] = useState<ActivityLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  
  // Filters
  const [actionFilter, setActionFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadActivities();
  }, [page, rowsPerPage, actionFilter]);

  const loadActivities = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const params = new URLSearchParams();
      params.append('limit', rowsPerPage.toString());
      params.append('offset', (page * rowsPerPage).toString());
      if (actionFilter) {
        params.append('action', actionFilter);
      }
      
      const response = await api.get<ActivityLogResponse>(`/account/activity/?${params.toString()}`);
      setActivities(response.data.activities);
      setTotal(response.data.total);
    } catch (err: any) {
      console.error('Failed to load activities:', err);
      setError('Failed to load activity log. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const getActionChip = (action: string, actionDisplay: string) => {
    const config = actionConfig[action] || { 
      icon: <EditIcon fontSize="small" />, 
      color: '#757575', 
      bgColor: '#F5F5F5' 
    };
    
    return (
      <Chip
        icon={config.icon as React.ReactElement}
        label={actionDisplay}
        size="small"
        sx={{
          backgroundColor: config.bgColor,
          color: config.color,
          fontWeight: 500,
          '& .MuiChip-icon': {
            color: config.color,
          },
        }}
      />
    );
  };

  // Filter activities by search query
  const filteredActivities = activities.filter(activity => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      activity.action_display.toLowerCase().includes(query) ||
      activity.resource_type.toLowerCase().includes(query) ||
      activity.resource_name.toLowerCase().includes(query) ||
      (activity.ip_address && activity.ip_address.includes(query))
    );
  });

  // Calculate stats
  const todayCount = activities.filter(a => {
    const activityDate = new Date(a.created_at);
    const today = new Date();
    return activityDate.toDateString() === today.toDateString();
  }).length;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Activity Log
        </Typography>
        <Typography variant="body1" color="text.secondary">
          View your recent account activity and actions
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                Total Activities
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                Today's Activities
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                {todayCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                Current Page
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {filteredActivities.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search activities..."
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 250 }}
          />
          
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Action Type</InputLabel>
            <Select
              value={actionFilter}
              label="Action Type"
              onChange={(e) => {
                setActionFilter(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">All Actions</MenuItem>
              <MenuItem value="login">Login</MenuItem>
              <MenuItem value="logout">Logout</MenuItem>
              <MenuItem value="login_failed">Login Failed</MenuItem>
              <MenuItem value="audit_created">Review Created</MenuItem>
              <MenuItem value="audit_completed">Review Completed</MenuItem>
              <MenuItem value="template_created">Template Created</MenuItem>
              <MenuItem value="team_invite">Team Invite</MenuItem>
              <MenuItem value="permission_change">Permission Change</MenuItem>
              <MenuItem value="data_export">Data Export</MenuItem>
              <MenuItem value="password_reset">Password Reset</MenuItem>
              <MenuItem value="profile_update">Profile Update</MenuItem>
            </Select>
          </FormControl>
          
          <Tooltip title="Refresh">
            <IconButton onClick={loadActivities} disabled={isLoading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Activity Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Date & Time</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Resource</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Details</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>IP Address</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : filteredActivities.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                    <Typography color="text.secondary">
                      No activities found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredActivities.map((activity) => (
                  <TableRow 
                    key={activity.id}
                    hover
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(activity.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {getActionChip(activity.action, activity.action_display)}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {activity.resource_type || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          maxWidth: 250, 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {activity.resource_name || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                        {activity.ip_address || '-'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Privacy Note */}
      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Activity logs are retained for security and compliance purposes. 
          You can request a copy of your data from the{' '}
          <a href="/my-data" style={{ color: '#E53935' }}>My Data</a> page.
        </Typography>
      </Box>
    </Container>
  );
};

export default ActivityLog;

